import { useCallback } from 'react';
import {
  useTabsStore, setRequestTabs, setCollectionTabs, setSchemaTabs, setTabStripOrder,
  setActiveRequestTabId, setActiveCollectionTabId, setActiveSchemaTabId, setActiveDbManagerTabId,
  setActiveTaskManagerTabId, setActiveProjectsTabId, setActiveDocumentTabId, setActiveTabType,
  setUrlFocusNonce, setDbQueryPickerOpen, guardDirtyDbTab,
} from '../store/tabsStore';
import {
  useCollectionsStore, setSelectedCollection, setSelectedRequest, setSelectedFolder,
  setSelectedSchemaEntry,
} from '../store/collectionsStore';
import { useUiModalStore, setActiveTab } from '../store/uiModalStore';
import { normalizeRequestBodyMode } from '../utils/requestBodyMode';
import { defaultRequestAuth } from '../utils/requestAuth';
import { protocolFieldsOf } from '../utils/requestProtocolFields';
import { hydrateCollectionTabFromRecord, isTabFor } from '../utils/collectionTabState';
import { normalizeMock, appendTabStripEntry } from '../utils/workspaceTabPersistence';
import { blankRequestOfKind } from '../utils/requestKinds';
import { useLiveConnectionTabSync } from './useLiveConnectionTabSync';
import { newTabId } from './tabs/tabIds';
import { useWorkspaceTabRestore } from './tabs/useWorkspaceTabRestore';
import { useTabClosing } from './tabs/useTabClosing';

/**
 * Tab lifecycle + persistence handlers (request/collection/schema tabs).
 * Tab state lives in tabsStore; collections/workspace read via getState().
 * The persistence/hydration EFFECTS stay in App and call these handlers — the
 * gating refs they share (which prevent the outgoing workspace's tabs from
 * being written into the incoming one on a switch) are passed in so App's
 * effects and these handlers agree on them.
 *
 * @param {{
 *   persistTabsTimeoutRef: { current: any },
 *   workspaceTabStateRef: { current: any },
 *   initializedRef: { current: boolean },
 *   tabsHydratingRef: { current: boolean },
 * }} deps
 */
export function useTabs({
  persistTabsTimeoutRef, workspaceTabStateRef, initializedRef, tabsHydratingRef,
}) {
  useLiveConnectionTabSync(); // a WebSocket lives as long as its tab

  const createNewRequestTab = useCallback((requestData = null) => {
    const id = newTabId();
    const newTab = {
      id,
      name: requestData?.name || null,
      method: requestData?.method || 'GET',
      url: requestData?.url || '',
      headers: requestData?.headers || [{ key: '', value: '', enabled: true }],
      params: requestData?.params || [{ key: '', value: '', enabled: true }],
      cookies: requestData?.cookies || [{ key: '', value: '', enabled: true }],
      body: requestData?.body || '',
      ...(() => {
        const mode = normalizeRequestBodyMode(requestData || {});
        return { bodyType: mode.bodyType, rawBodyLanguage: mode.rawBodyLanguage };
      })(),
      formDataFields: requestData?.formDataFields ?? undefined,
      auth: requestData?.auth || defaultRequestAuth(),
      preRequestScript: requestData?.preRequestScript ?? '',
      testScript: requestData?.testScript ?? '',
      mock: normalizeMock(requestData?.mock),
      // A bare new tab (no requestData) starts clean — the unsaved dot only
      // appears once the user edits it. Requests opened/imported with content
      // but no backend id still start unsaved so they prompt a save.
      hasUnsavedChanges: requestData?.hasUnsavedChanges ?? (requestData ? !requestData.id : false),
      response: requestData?.response || null,
      loading: false,
      collectionId: requestData?.collectionId || null,
      folderId: requestData?.folderId || null,
      originalId: requestData?.id || null, // Backend ID for existing requests
      isNewRequest: !requestData?.id, // Flag to indicate if this is a new unsaved request
      editorTab: requestData?.editorTab ?? 'params',
      responseTab: requestData?.responseTab ?? 'body',
      type: requestData?.type || 'http',
      // gRPC / WebSocket settings of a saved request (card 5258c71d) — this list left them out,
      // so a request opened from the tree showed an empty address / no headers.
      ...protocolFieldsOf(requestData),
    };

    setRequestTabs(prev => [...prev, newTab]);
    setTabStripOrder((prev) => appendTabStripEntry(prev, { type: 'request', id }));
    setActiveRequestTabId(newTab.id);
    setActiveTabType('request');
    return newTab;
  }, []);

  const openCollectionTab = useCallback((collection) => {
    if (!collection?.id) return;

    // The collection's own page, not one of its folder pages (owner D1).
    const existing = useTabsStore.getState().collectionTabs.find((tab) => isTabFor(tab, collection.id));
    if (existing) {
      setActiveCollectionTabId(existing.id);
      setActiveTabType('collection');
      setSelectedCollection(collection);
      setSelectedRequest(null);
      setSelectedFolder(null);
      setSelectedSchemaEntry(null);
      return existing;
    }

    const tabId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `col-tab-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

    const hydrated = hydrateCollectionTabFromRecord(collection);
    const newTab = {
      id: tabId,
      type: 'collection',
      ...hydrated,
    };

    setCollectionTabs((prev) => [...prev, newTab]);
    setTabStripOrder((prev) => appendTabStripEntry(prev, { type: 'collection', id: tabId }));
    setActiveCollectionTabId(tabId);
    setActiveTabType('collection');
    setSelectedCollection(collection);
    setSelectedRequest(null);
    setSelectedFolder(null);
    setSelectedSchemaEntry(null);
    return newTab;
  }, []);

  // ---- Smart Mock: Schemas (tree + editor tabs) -------------------------
  const openSchemaTab = useCallback((schemaItem) => {
    if (!schemaItem?.id) return;
    const existing = useTabsStore.getState().schemaTabs.find(
      (tab) => String(tab.schemaId) === String(schemaItem.id)
    );
    if (existing) {
      setActiveSchemaTabId(existing.id);
      setActiveTabType('schema');
      return existing;
    }
    const tabId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `sch-tab-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const newTab = {
      id: tabId,
      type: 'schema',
      schemaId: schemaItem.id,
      name: schemaItem.name || 'Schema',
      rootType: schemaItem.rootType === 'array' ? 'array' : 'object',
      nodes: Array.isArray(schemaItem.nodes) ? schemaItem.nodes : [],
      hasUnsavedChanges: false,
    };
    setSchemaTabs((prev) => [...prev, newTab]);
    setTabStripOrder((prev) => appendTabStripEntry(prev, { type: 'schema', id: tabId }));
    setActiveSchemaTabId(tabId);
    setActiveTabType('schema');
    return newTab;
  }, []);

  const getCurrentRequestTab = useCallback(() => {
    const { requestTabs, activeRequestTabId } = useTabsStore.getState();
    return requestTabs.find((tab) => tab.id === activeRequestTabId);
  }, []);

  // Create stable versions that use the store to prevent re-render loops
  const updateCurrentRequestTabStable = useCallback((updates) => {
    setRequestTabs(prev => prev.map(tab =>
      tab.id === useTabsStore.getState().activeRequestTabId
        ? { ...tab, ...updates, hasUnsavedChanges: true }
        : tab
    ));
  }, []);

  // Update response and loading without marking as unsaved changes
  const updateCurrentRequestTabResponse = useCallback((updates) => {
    setRequestTabs(prev => prev.map(tab =>
      tab.id === useTabsStore.getState().activeRequestTabId
        ? { ...tab, ...updates }
        : tab
    ));
  }, []);

  // Clear loading state for a specific tab by ID (used when switching away mid-request)
  const clearTabLoading = useCallback((tabId) => {
    if (!tabId) return;
    setRequestTabs(prev => prev.map(tab =>
      tab.id === tabId ? { ...tab, loading: false } : tab
    ));
  }, []);

  const {
    closeTab, duplicateTab, closeOtherTabs, closeAllTabs,
  } = useTabClosing({ createNewRequestTab });

  const {
    flushWorkspaceTabPersist, scheduleWorkspaceTabPersist, handleTabsReorder,
    applyWorkspaceTabSnapshot, restoreWorkspaceTabs,
  } = useWorkspaceTabRestore({
    persistTabsTimeoutRef, workspaceTabStateRef, initializedRef, tabsHydratingRef, closeAllTabs,
  });

  const handleTabChange = useCallback((tabId, tabType) => {
    // Leaving a DB Manager table tab with staged grid edits discards them (the
    // arriving tab refetches, and the edits are keyed by row index into the old
    // result set). Ask first. Re-selecting the tab already active is not a
    // departure, so it passes straight through.
    const leavingActiveDbTab = !(
      tabType === 'db-manager-table' && tabId === useTabsStore.getState().activeDbManagerTabId
    );
    if (leavingActiveDbTab && guardDirtyDbTab(() => handleTabChange(tabId, tabType))) return;
    if (tabType === 'request') {
      setActiveRequestTabId(tabId);
      setActiveTabType('request');
      setSelectedCollection(null);
      setSelectedFolder(null);
      setSelectedSchemaEntry(null);
      const collections = useCollectionsStore.getState().collections;
      const tab = useTabsStore.getState().requestTabs.find((t) => String(t.id) === String(tabId));
      if (tab?.originalId != null) {
        const req = collections
          .flatMap((c) => c.requests || [])
          .find((r) => String(r.id) === String(tab.originalId));
        setSelectedRequest(req || null);
      } else {
        setSelectedRequest(null);
      }
    } else if (tabType === 'collection') {
      setActiveCollectionTabId(tabId);
      setActiveTabType('collection');
      setSelectedSchemaEntry(null);
      const collections = useCollectionsStore.getState().collections;
      const tab = useTabsStore.getState().collectionTabs.find((t) => t.id === tabId);
      if (tab) {
        const col = collections.find((c) => String(c.id) === String(tab.collectionId));
        setSelectedCollection(col || null);
        setSelectedRequest(null);
        setSelectedFolder(null);
      }
    } else if (tabType === 'schema') {
      setActiveSchemaTabId(tabId);
      setActiveTabType('schema');
      setSelectedCollection(null);
      setSelectedRequest(null);
      setSelectedFolder(null);
      setSelectedSchemaEntry(null);
    } else if (tabType === 'db-manager-table') {
      setActiveDbManagerTabId(tabId);
      setActiveTabType('db-manager-table');
      setSelectedCollection(null);
      setSelectedRequest(null);
      setSelectedFolder(null);
      setSelectedSchemaEntry(null);
    } else if (tabType === 'task-manager-board') {
      setActiveTaskManagerTabId(tabId);
      setActiveTabType('task-manager-board');
      setSelectedCollection(null);
      setSelectedRequest(null);
      setSelectedFolder(null);
      setSelectedSchemaEntry(null);
    } else if (tabType === 'projects-project') {
      setActiveProjectsTabId(tabId);
      setActiveTabType('projects-project');
      setSelectedCollection(null);
      setSelectedRequest(null);
      setSelectedFolder(null);
      setSelectedSchemaEntry(null);
    } else if (tabType === 'document-editor') {
      setActiveDocumentTabId(tabId);
      setActiveTabType('document-editor');
      setSelectedCollection(null);
      setSelectedRequest(null);
      setSelectedFolder(null);
      setSelectedSchemaEntry(null);
    }
  }, []);

  // `kind`: 'websocket' | 'sse' | 'grpc' from the chevron menu; anything else (nothing,
  // a click event, Ctrl+T) is a plain HTTP request — see utils/requestKinds.
  const handleNewRequestTab = useCallback((kind) => {
    // While browsing DB Manager, the tab strip's + button opens a blank SQL
    // query tab against the same connection instead of a request tab — a
    // request tab only makes sense in the Collections/API-request context.
    if (useTabsStore.getState().activeTabType === 'db-manager-table') {
      // A new query tab starts with the database picker modal (host +
      // database list of every live connection) — never falls through to a
      // request tab in this scope (the DB-scoped strip filters request tabs
      // out, which would render a builder with no visible tab).
      setDbQueryPickerOpen(true);
      return;
    }
    // Task Manager has no generic "new sub-tab" content — jump to the pinned
    // "Boards" tab so the user can pick another board to open from there.
    if (useTabsStore.getState().activeTabType === 'task-manager-board') {
      const pinned = useTabsStore.getState().taskManagerTabs.find((t) => t.pinned);
      if (pinned) {
        setActiveTaskManagerTabId(pinned.id);
        setActiveTabType('task-manager-board');
      }
      return;
    }
    // Projects, like Boards: Ctrl+T / the palette's New Tab jump to the pinned
    // "Projects" tab instead of creating a request tab this strip never shows.
    if (useTabsStore.getState().activeTabType === 'projects-project') {
      const pinned = useTabsStore.getState().projectsTabs.find((t) => t.pinned);
      if (pinned) {
        setActiveProjectsTabId(pinned.id);
        setActiveTabType('projects-project');
      }
      return;
    }
    // Teams replaces the tab strip + builder; jump back so the new tab is visible and URL can focus.
    if (useUiModalStore.getState().activeTab === 'teams') {
      setActiveTab('collections');
    }
    // Create a new request tab without associating it to any collection
    // No collection association for the + button; no backend id until it is saved
    createNewRequestTab(blankRequestOfKind(kind));
    setUrlFocusNonce((n) => n + 1);

    // Mark the tab as having unsaved changes since it's a new request
    setTimeout(() => {
      updateCurrentRequestTabStable({ hasUnsavedChanges: true });
    }, 0);
  }, [createNewRequestTab, updateCurrentRequestTabStable]);

  return {
    createNewRequestTab, openCollectionTab, openSchemaTab, getCurrentRequestTab,
    updateCurrentRequestTabStable, updateCurrentRequestTabResponse, clearTabLoading, closeTab,
    duplicateTab, closeOtherTabs, closeAllTabs, flushWorkspaceTabPersist,
    scheduleWorkspaceTabPersist, handleTabsReorder, applyWorkspaceTabSnapshot, restoreWorkspaceTabs,
    handleTabChange, handleNewRequestTab,
  };
}
